import posthog from 'posthog-js'

import type { AuthUser } from '@/hooks/useAuth'
import { getEnvironmentConfig } from '@/lib/environment'

class PostHogTrackingService {
  private lastIdentifiedUserId: string | null = null
  private lastIdentifiedProperties: {
    email?: string
    name?: string
  } | null = null

  private readonly postHogKey: string
  private readonly postHogHost: string

  constructor() {
    this.postHogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY || ''
    this.postHogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || ''
  }

  get isPostHogAvailable(): boolean {
    return !!this.postHogKey && !!this.postHogHost
  }

  init(): void {
    const { isProduction } = getEnvironmentConfig()

    if (!this.isPostHogAvailable) {
      return
    }

    posthog.init(this.postHogKey, {
      api_host: this.postHogHost,
      defaults: '2025-05-24',
      cookieless_mode: 'on_reject',
      capture_pageview: 'history_change',
      capture_pageleave: true,
      autocapture: true,
      debug: !isProduction,
    })
  }

  updateConsent(preferences: Record<string, boolean>): void {
    if (!this.isPostHogAvailable) {
      return
    }

    try {
      const hasMeasurementConsent = preferences.measurement === true

      if (hasMeasurementConsent) {
        posthog.opt_in_capturing()
        return
      }

      posthog.opt_out_capturing()
    } catch (error) {
      console.error('PostHog consent handling failed:', error)
    }
  }

  identifyUser(user: AuthUser): void {
    if (!this.isPostHogAvailable) {
      return
    }

    try {
      const email = user.primaryEmailAddress?.emailAddress ?? ''
      const name =
        `${user.firstName || ''} ${user.lastName || ''}`.trim() || undefined
      const properties = {
        email,
        name,
        signup_time: user.createdAt?.getTime(),
        is_emergn_employee: email.toLowerCase().endsWith('@emergn.com'),
      }

      if (
        this.lastIdentifiedUserId === user.id &&
        this.lastIdentifiedProperties?.email === properties.email &&
        this.lastIdentifiedProperties?.name === properties.name
      ) {
        return
      }

      posthog.identify(user.id, properties)

      this.lastIdentifiedUserId = user.id
      this.lastIdentifiedProperties = properties
    } catch (error) {
      console.error('PostHog identify failed:', error)
    }
  }

  reset(): void {
    if (!this.isPostHogAvailable) {
      return
    }

    try {
      posthog.reset()

      this.lastIdentifiedUserId = null
      this.lastIdentifiedProperties = null
    } catch (error) {
      console.error('PostHog reset failed:', error)
    }
  }

  willCapture(): boolean {
    if (!this.isPostHogAvailable) {
      return false
    }

    try {
      return posthog.is_capturing?.() ?? false
    } catch (error) {
      console.error('Error checking PostHog capture status:', error)
      return false
    }
  }

  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.isPostHogAvailable) {
      return
    }

    try {
      posthog.capture(eventName, properties)
    } catch (error) {
      console.error(`Error tracking event ${eventName}:`, error)
    }
  }

  getDistinctId(): string | undefined {
    return posthog.get_distinct_id() || undefined
  }

  getSessionId(): string | undefined {
    return posthog.get_session_id() || undefined
  }
}

const postHogTrackingService = new PostHogTrackingService()

export { PostHogTrackingService, postHogTrackingService }
